import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import {
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Autocomplete,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  FormControlLabel,
  Switch,
  FormControl,
  RadioGroup,
  Radio,
  FormLabel,
  Chip,
  Collapse,
  Divider,
  InputAdornment,
  Tooltip,
} from '@mui/material';
import MobileDialog from './MobileDialog';
import PaymentMethodRows from '../components/PaymentMethodRows';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import heLocale from 'date-fns/locale/he';
import { format } from 'date-fns';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import API from '../../services/api';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useUser } from '../../contexts/UserContext';

const CURRENCY_SYMBOLS = { ILS: '₪', USD: '$', EUR: '€' };

const SupplierPurchaseDialog = ({ open, onClose, supplier, onSuccess }) => {
  const { userProfile } = useUser();
  const queryClient = useQueryClient();
  const storageEnabled = userProfile?.group?.order_config?.storage_config?.enabled || false;
  const barcodeEnabled = userProfile?.group?.barcode_config?.enabled === true;

  const isNonILS = supplier?.Currency && supplier.Currency !== 'ILS';
  const currencySymbol = CURRENCY_SYMBOLS[supplier?.Currency] || '₪';

  // Get product fields configuration
  const productFields = userProfile?.group?.ui_config?.product_fields || [];
  const productFieldsSchema = userProfile?.group?.ui_config?.product_fields_schema || {};

  const [actualDate, setActualDate] = useState(new Date());
  const [items, setItems] = useState([]);
  const [shippingCost, setShippingCost] = useState('');
  const [notes, setNotes] = useState('');
  const [updateInventory, setUpdateInventory] = useState(true);
  const [specifyItems, setSpecifyItems] = useState(false);
  const [totalAmount, setTotalAmount] = useState('');
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [shippingDocument, setShippingDocument] = useState('');

  // Optional "pay now" section state — when enabled, a payment transaction is created
  // alongside the purchase in a single atomic backend call.
  const [recordPaymentNow, setRecordPaymentNow] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentNote, setPaymentNote] = useState('');
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [paymentTouched, setPaymentTouched] = useState(false);

  // Exchange rate for non-ILS suppliers
  const [exchangeRate, setExchangeRate] = useState('');

  // Tax state
  const [taxState, setTaxState] = useState('none'); // none, included, excluded
  const [taxRate, setTaxRate] = useState(18.00);
  const [taxAmount, setTaxAmount] = useState(0);
  const [netAmount, setNetAmount] = useState(0);

  // Current item being added
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [quantity, setQuantity] = useState('');
  const [unitPrice, setUnitPrice] = useState('');
  const [selectedStorage, setSelectedStorage] = useState(null);
  const [itemNotes, setItemNotes] = useState('');

  // Product creation state
  const [isCreatingProduct, setIsCreatingProduct] = useState(false);
  const [newProductData, setNewProductData] = useState({
    ProductName: '',
    Price: '',
    Description: '',
    CatalogNumber: '',
    Barcode: '',
  });
  const [createProductError, setCreateProductError] = useState('');
  const [generateBarcodeOnSave, setGenerateBarcodeOnSave] = useState(false);

  const [formStep, setFormStep] = useState('form');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isSubmittingRef = useRef(false);
  const errorRef = useRef(null);

  // Scroll to error when it appears
  useEffect(() => {
    if (error && errorRef.current) {
      errorRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [error]);

  // Fetch products
  const { data: products = [], isLoading: isLoadingProducts } = useQuery({
    queryKey: ['products'],
    queryFn: async () => {
      const response = await API.get('/products/');
      return response.data;
    },
    enabled: open,
  });

  // Fetch storages
  const { data: storages = [], isLoading: isLoadingStorages } = useQuery({
    queryKey: ['storages'],
    queryFn: async () => {
      const response = await API.get('/storages/');
      return response.data;
    },
    enabled: open && storageEnabled,
  });

  // Mutation for creating a new product. If `generateBarcodeOnSave` is
  // set, it makes a follow-up call to /barcode/generate/ to mint a new
  // barcode and attaches it to the product before returning.
  const createProductMutation = useMutation({
    mutationFn: async (productData) => {
      const response = await API.post('/products/create/', productData);
      let product = response.data;
      if (generateBarcodeOnSave && barcodeEnabled && !product.Barcode) {
        try {
          const { data: genData } = await API.post('/barcode/generate/', {
            ProductID: product.ProductID,
          });
          product = { ...product, Barcode: genData.Barcode };
        } catch (e) {
          // Non-fatal — product exists, barcode generation failed.
          // eslint-disable-next-line no-console
          console.warn('Barcode generation after create failed:', e);
        }
      }
      return product;
    },
    onSuccess: (data) => {
      // Invalidate products query to refetch the list
      queryClient.invalidateQueries(['products']);

      // Auto-fill the product data into the item form
      setSelectedProduct(data);
      setUnitPrice(data.Price || '');
      setQuantity('1');

      // Reset product creation form
      setIsCreatingProduct(false);
      setGenerateBarcodeOnSave(false);
      setNewProductData({
        ProductName: '',
        Price: '',
        Description: '',
        CatalogNumber: '',
        Barcode: '',
      });
      setCreateProductError('');
    },
    onError: (error) => {
      setCreateProductError(error?.response?.data?.error || 'שגיאה ביצירת מוצר');
    },
  });

  // Handler for new product form changes
  const handleNewProductChange = (e) => {
    const { name, value } = e.target;
    setNewProductData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  // Handler to create new product
  const handleCreateProduct = () => {
    setCreateProductError('');

    // Validation
    if (!newProductData.ProductName || newProductData.ProductName.trim() === '') {
      setCreateProductError('נא להזין שם מוצר');
      return;
    }

    const price = newProductData.Price === '' ? 0 : parseFloat(newProductData.Price);
    if (isNaN(price) || price < 0) {
      setCreateProductError('נא להזין מחיר תקין (0 או יותר)');
      return;
    }

    // Prepare data - only send non-empty values
    const productData = {
      ProductName: newProductData.ProductName.trim(),
      Price: Math.round(price * 100) / 100,
    };

    if (newProductData.Description && newProductData.Description.trim()) {
      productData.Description = newProductData.Description.trim();
    }

    if (productFields.includes('CatalogNumber') && newProductData.CatalogNumber && newProductData.CatalogNumber.trim()) {
      productData.CatalogNumber = newProductData.CatalogNumber.trim();
    }

    if (
      productFields.includes('Barcode') &&
      !generateBarcodeOnSave &&
      newProductData.Barcode &&
      newProductData.Barcode.trim()
    ) {
      productData.Barcode = newProductData.Barcode.trim();
    }

    createProductMutation.mutate(productData);
  };

  // Toggle product creation mode
  const toggleProductCreationMode = () => {
    setIsCreatingProduct(!isCreatingProduct);
    setCreateProductError('');
    if (!isCreatingProduct) {
      // Entering creation mode - reset form
      setNewProductData({
        ProductName: '',
        Price: '',
        Description: '',
        CatalogNumber: '',
        Barcode: '',
      });
    }
  };

  // Enhanced product filtering function
  const filterProductsEnhanced = useCallback((options, { inputValue }) => {
    const input = inputValue.toLowerCase().trim();

    if (input.length < 2) {
      return [];
    }

    // Check if barcode and catalog number are in the UI config
    const showBarcode = productFields.includes('Barcode');
    const showCatalogNumber = productFields.includes('CatalogNumber');
    const showEnhancedSearch = showBarcode || showCatalogNumber || storageEnabled;

    const filtered = options.map(product => {
      const productName = (product.ProductName || '').toLowerCase();
      const productId = (product.ProductID || '').toString();

      // Basic search (always active)
      let matches = productName.includes(input) || productId.includes(input);
      let matchedField = null;

      // Enhanced search (only if fields are in UI config or storage enabled)
      if (showEnhancedSearch) {
        const barcode = showBarcode ? (product.Barcode || '').toLowerCase() : '';
        const catalogNumber = showCatalogNumber ? (product.CatalogNumber || '').toLowerCase() : '';

        // Check which field matches
        if (productName.includes(input)) {
          matchedField = 'שם מוצר';
        } else if (showCatalogNumber && catalogNumber.includes(input)) {
          matchedField = productFieldsSchema.CatalogNumber?.label || 'מק"ט';
        } else if (showBarcode && barcode.includes(input)) {
          matchedField = productFieldsSchema.Barcode?.label || 'ברקוד';
        } else if (productId.includes(input)) {
          matchedField = 'מזהה';
        }

        matches = matches ||
                  (showBarcode && barcode.includes(input)) ||
                  (showCatalogNumber && catalogNumber.includes(input));
      }

      if (matches) {
        return showEnhancedSearch ? { ...product, matchedField } : product;
      }
      return null;
    }).filter(Boolean);

    return filtered.slice(0, 50);
  }, [productFields, productFieldsSchema, storageEnabled]);

  // Auto-select default storage
  useEffect(() => {
    if (storages.length > 0 && !selectedStorage) {
      const defaultStorage = storages.find(s => s.is_default);
      if (defaultStorage) {
        setSelectedStorage(defaultStorage);
      }
    }
  }, [storages, selectedStorage]);

  // Clear storage from items when updateInventory is toggled off
  useEffect(() => {
    if (!updateInventory && items.length > 0) {
      setItems(items.map(item => ({
        ...item,
        Storage: null,
        StorageName: '',
      })));
    }
  }, [updateInventory]);

  // Fetch exchange rate when dialog opens for non-ILS supplier
  useEffect(() => {
    if (open && isNonILS) {
      API.get(`/supplier/exchange-rate/?currency=${supplier.Currency}`)
        .then((res) => setExchangeRate(String(res.data.rate)))
        .catch(() => {
          API.get(`/supplier/transactions/?supplier_id=${supplier.SupplierID}`)
            .then((res) => {
              const lastForeign = res.data?.find(t => t.ExchangeRate && parseFloat(t.ExchangeRate) !== 1);
              if (lastForeign) setExchangeRate(String(lastForeign.ExchangeRate));
            })
            .catch(() => {});
        });
    }
  }, [open, supplier?.SupplierID, supplier?.Currency, isNonILS]);

  const resetForm = () => {
    setActualDate(new Date());
    setItems([]);
    setShippingCost('');
    setNotes('');
    setUpdateInventory(true);
    setSpecifyItems(false);
    setTotalAmount('');
    setInvoiceNumber('');
    setShippingDocument('');
    setSelectedProduct(null);
    setQuantity('');
    setUnitPrice('');
    setItemNotes('');
    setFormStep('form');
    setError('');
    setExchangeRate('');
    setTaxState('none');
    setTaxRate(18.00);
    setTaxAmount(0);
    setNetAmount(0);
    setRecordPaymentNow(false);
    setPaymentAmount('');
    setPaymentNote('');
    setPaymentMethods([]);
    setPaymentTouched(false);
    setIsCreatingProduct(false);
    setNewProductData({
      ProductName: '',
      Price: '',
      Description: '',
      CatalogNumber: '',
      Barcode: '',
    });
    setCreateProductError('');
    isSubmittingRef.current = false;
    setIsSubmitting(false);
  };

  const handleClose = () => {
    if (isSubmitting) return;
    resetForm();
    onClose();
  };

  const handleAddItem = () => {
    if (!selectedProduct) {
      setError('יש לבחור מוצר');
      return;
    }
    const qty = parseFloat(quantity);
    const price = parseFloat(unitPrice);
    if (Number.isNaN(qty) || qty <= 0) {
      setError('כמות חייבת להיות גדולה מאפס');
      return;
    }
    if (Number.isNaN(price) || price < 0) {
      setError('מחיר יחידה חייב להיות 0 או יותר');
      return;
    }

    const newItem = {
      Product: selectedProduct.ProductID,
      ProductName: selectedProduct.ProductName,
      Quantity: qty,
      UnitPrice: price,
      Storage: updateInventory ? (selectedStorage?.StorageID || null) : null,
      StorageName: updateInventory ? (selectedStorage?.StorageName || 'ברירת מחדל') : '',
      Notes: itemNotes,
      Subtotal: qty * price,
    };

    setItems([...items, newItem]);
    setSelectedProduct(null);
    setQuantity('');
    setUnitPrice('');
    setItemNotes('');
    setError('');
  };

  const handleRemoveItem = (index) => {
    setItems(items.filter((_, i) => i !== index));
  };

  const calculateSubtotal = () => {
    return items.reduce((sum, item) => sum + item.Subtotal, 0);
  };

  // Calculate tax and totals based on tax state
  useEffect(() => {
    let calculatedTaxAmount = 0;
    let calculatedNetAmount = 0;

    if (specifyItems) {
      // For itemized purchases
      const subtotal = calculateSubtotal();
      const shipping = parseFloat(shippingCost) || 0;
      const amountBeforeTax = subtotal + shipping;

      if (taxState === 'none') {
        calculatedNetAmount = amountBeforeTax;
        calculatedTaxAmount = 0;
      } else if (taxState === 'included') {
        calculatedNetAmount = Math.round((amountBeforeTax / (1 + taxRate / 100)) * 100) / 100;
        calculatedTaxAmount = Math.round((amountBeforeTax - calculatedNetAmount) * 100) / 100;
      } else if (taxState === 'excluded') {
        calculatedNetAmount = amountBeforeTax;
        calculatedTaxAmount = Math.round((amountBeforeTax * (taxRate / 100)) * 100) / 100;
      }
    } else {
      // For simple mode with total amount
      const enteredTotal = parseFloat(totalAmount) || 0;

      if (taxState === 'none') {
        calculatedNetAmount = enteredTotal;
        calculatedTaxAmount = 0;
      } else if (taxState === 'included') {
        // Extract tax from the entered total
        calculatedNetAmount = Math.round((enteredTotal / (1 + taxRate / 100)) * 100) / 100;
        calculatedTaxAmount = Math.round((enteredTotal - calculatedNetAmount) * 100) / 100;
      } else if (taxState === 'excluded') {
        // Add tax on top of entered amount
        calculatedNetAmount = enteredTotal;
        calculatedTaxAmount = Math.round((enteredTotal * (taxRate / 100)) * 100) / 100;
      }
    }

    setTaxAmount(calculatedTaxAmount);
    setNetAmount(calculatedNetAmount);
  }, [items, shippingCost, taxState, taxRate, specifyItems, totalAmount]);

  const calculateTotal = () => {
    if (specifyItems) {
      // For itemized purchases
      const subtotal = calculateSubtotal();
      const shipping = parseFloat(shippingCost) || 0;
      const amountBeforeTax = subtotal + shipping;

      if (taxState === 'none' || taxState === 'included') {
        return amountBeforeTax;
      } else if (taxState === 'excluded') {
        return amountBeforeTax + taxAmount;
      }
      return amountBeforeTax;
    } else {
      // For simple mode
      const enteredTotal = parseFloat(totalAmount) || 0;

      if (taxState === 'none' || taxState === 'included') {
        // For included, the entered amount is already the total
        return enteredTotal;
      } else if (taxState === 'excluded') {
        // Add calculated tax to entered amount
        return enteredTotal + taxAmount;
      }
      return enteredTotal;
    }
  };

  // Auto-default the "pay now" amount to the calculated purchase total when the toggle is
  // on, unless the user has manually edited it.
  useEffect(() => {
    if (!recordPaymentNow || paymentTouched) return;
    const total = calculateTotal();
    if (total > 0) {
      setPaymentAmount(total.toFixed(2));
    } else {
      setPaymentAmount('');
    }
    // calculateTotal() depends on the inputs below; include them in the dep array.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recordPaymentNow, paymentTouched, items, shippingCost, taxState, taxRate, taxAmount, specifyItems, totalAmount]);

  const handleValidation = () => {
    setError('');
    
    if (specifyItems) {
      // Validating with items
      if (items.length === 0) {
        setError('יש להוסיף לפחות פריט אחד או לבטל את "פירוט מוצרים"');
        return;
      }
    } else {
      // Validating without items - just total amount
      const total = parseFloat(totalAmount);
      if (Number.isNaN(total) || total <= 0) {
        setError('יש להזין סכום כולל גדול מאפס');
        return;
      }
    }

    // Validate optional "pay now" section
    if (recordPaymentNow) {
      const payAmt = parseFloat(paymentAmount);
      if (Number.isNaN(payAmt) || payAmt <= 0) {
        setError('סכום התשלום חייב להיות גדול מאפס');
        return;
      }
      const purchaseTotal = calculateTotal();
      if (payAmt - purchaseTotal > 0.01) {
        setError('סכום התשלום לא יכול לעלות על סכום הרכישה');
        return;
      }
      if (paymentMethods.length > 0) {
        const pmTotal = paymentMethods.reduce((sum, pm) => sum + (parseFloat(pm.amount) || 0), 0);
        if (Math.abs(pmTotal - payAmt) > 0.01) {
          setError('סכום אמצעי התשלום אינו תואם לסכום התשלום');
          return;
        }
        const invalidChecks = paymentMethods.filter(
          pm => pm.type === 'check' && !pm.check_number
        );
        if (invalidChecks.length > 0) {
          setError("יש להזין מספר צ'ק לכל תשלום בצ'ק");
          return;
        }
      }
    }

    setFormStep('confirm');
  };

  const handleSubmit = async () => {
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;
    setIsSubmitting(true);
    setError('');

    try {
      let payload;
      
      if (specifyItems && items.length > 0) {
        // Mode 1: With detailed items
        const subtotal = calculateSubtotal();
        const shipping = parseFloat(shippingCost) || 0;
        const total = calculateTotal();

        payload = {
          supplier_id: supplier.SupplierID,
          actual_date: format(actualDate, 'yyyy-MM-dd'),
          subtotal: subtotal,
          is_taxable: taxState !== 'none',
          is_tax_included: taxState === 'included',
          tax_rate: taxRate,
          tax_amount: taxAmount,
          net_amount: netAmount,
          shipping_cost: shipping,
          total_amount: total,
          notes: notes,
          update_inventory: storageEnabled && updateInventory,
          invoice_number: invoiceNumber,
          shipping_document: shippingDocument,
          items: items.map(item => ({
            product_id: item.Product,
            quantity: item.Quantity,
            unit_price: item.UnitPrice,
            storage_id: item.Storage,
            notes: item.Notes,
          })),
        };
      } else {
        // Mode 2: Simple purchase without items
        const enteredAmount = parseFloat(totalAmount);
        const finalTotal = calculateTotal();

        payload = {
          supplier_id: supplier.SupplierID,
          actual_date: format(actualDate, 'yyyy-MM-dd'),
          subtotal: enteredAmount,
          is_taxable: taxState !== 'none',
          is_tax_included: taxState === 'included',
          tax_rate: taxRate,
          tax_amount: taxAmount,
          net_amount: netAmount,
          shipping_cost: 0,
          total_amount: finalTotal,
          notes: notes,
          invoice_number: invoiceNumber,
          shipping_document: shippingDocument,
        };
      }

      // Add exchange rate for non-ILS suppliers
      if (isNonILS && exchangeRate) {
        payload.exchange_rate = parseFloat(exchangeRate);
      }

      // Attach optional "pay now" payment block — backend creates both records atomically
      if (recordPaymentNow) {
        payload.payment = {
          amount: parseFloat(paymentAmount),
          actual_date: format(actualDate, 'yyyy-MM-dd'),
          note: paymentNote,
          payment_methods: paymentMethods.map(pm => ({
            type: pm.type,
            amount: parseFloat(pm.amount) || 0,
            // Check fields
            check_number: pm.check_number || '',
            check_bank_name: pm.check_bank_name || '',
            check_account_number: pm.check_account_number || '',
            check_due_date: pm.check_due_date ? format(pm.check_due_date, 'yyyy-MM-dd') : null,
            // Card fields
            card_last_four: pm.card_last_four || '',
            card_type: pm.card_type || '',
            // Transfer fields
            bank_name: pm.bank_name || '',
            bank_account_last4: pm.bank_account_last4 || '',
            transfer_date: pm.transfer_date ? format(pm.transfer_date, 'yyyy-MM-dd') : null,
            notes: pm.notes || '',
          })),
        };
      }

      await API.post('/supplier/purchase/log/', payload);
      if (onSuccess) onSuccess();
      resetForm();
      onClose();
    } catch (err) {
      console.error('Error logging purchase:', err);
      setError(err?.response?.data?.error || 'שגיאה ברישום רכישה');
      setIsSubmitting(false);
      isSubmittingRef.current = false;
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={heLocale}>
      <MobileDialog
        open={open}
        onClose={(event, reason) => {
            if (reason === 'backdropClick') return;
            handleClose();
        }}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            fontSize: { xs: '1rem', sm: '1.4rem' },
            '*': { fontSize: 'inherit !important' },
          },
        }}
      >
        <DialogTitle sx={{ fontSize: '2rem !important', fontWeight: 'bold' }}>
          רישום רכישה מספק
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {error && <Alert ref={errorRef} severity="error">{error}</Alert>}

            {formStep === 'form' && (
              <>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                  ספק: {supplier?.SupplierName}
                  {isNonILS && ` (${supplier.Currency})`}
                </Typography>

                {isNonILS && (
                  <TextField
                    label={`שער חליפין (1 ${supplier.Currency} = ? ₪)`}
                    type="number"
                    fullWidth
                    value={exchangeRate}
                    onChange={(e) => setExchangeRate(e.target.value)}
                    onWheel={(e) => e.target.blur()}
                    onFocus={(e) => e.target.select()}
                    disabled={isSubmitting}
                    helperText="שער מעודכן מבנק ישראל - ניתן לשנות"
                    sx={{ direction: 'ltr', '& input': { textAlign: 'center' } }}
                  />
                )}

                <DatePicker
                  label="תאריך רכישה"
                  value={actualDate}
                  onChange={(newValue) => setActualDate(newValue)}
                  disabled={isSubmitting}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                    },
                  }}
                />

                <TextField
                  label="מספר חשבונית"
                  value={invoiceNumber}
                  onChange={(e) => setInvoiceNumber(e.target.value)}
                  fullWidth
                  disabled={isSubmitting}
                />

                <TextField
                  label="תעודת משלוח"
                  value={shippingDocument}
                  onChange={(e) => setShippingDocument(e.target.value)}
                  fullWidth
                  disabled={isSubmitting}
                />

                <FormControlLabel
                  control={
                    <Switch
                      checked={specifyItems}
                      onChange={(e) => setSpecifyItems(e.target.checked)}
                      disabled={isSubmitting}
                    />
                  }
                  label="פירוט מוצרים"
                />

                { storageEnabled && specifyItems && (
                  <>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={updateInventory}
                          onChange={(e) => setUpdateInventory(e.target.checked)}
                          disabled={isSubmitting}
                        />
                      }
                      label="עדכן מלאי"
                    />
                  </>
                )}

                { specifyItems ? (
                  <>
                    {/* Add Item Form */}
                    <Box sx={{ bgcolor: 'background.default', p: 2, borderRadius: 1 }}>
                      <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'bold' }}>
                        {isCreatingProduct ? 'יצירת מוצר חדש' : 'הוספת פריט'}
                      </Typography>

                      {isCreatingProduct ? (
                        <>
                          {/* Product Creation Form */}
                          {createProductError && (
                            <Alert severity="error" sx={{ mb: 2 }}>
                              {createProductError}
                            </Alert>
                          )}

                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <TextField
                              name="ProductName"
                              label="שם מוצר"
                              value={newProductData.ProductName}
                              onChange={handleNewProductChange}
                              variant="outlined"
                              fullWidth
                              required
                              disabled={createProductMutation.isLoading}
                            />

                            {productFields.includes('CatalogNumber') && (
                              <TextField
                                name="CatalogNumber"
                                label={productFieldsSchema.CatalogNumber?.label || 'מק"ט'}
                                value={newProductData.CatalogNumber}
                                onChange={handleNewProductChange}
                                variant="outlined"
                                fullWidth
                                disabled={createProductMutation.isLoading}
                              />
                            )}

                            {productFields.includes('Barcode') && (
                              <TextField
                                name="Barcode"
                                label={productFieldsSchema.Barcode?.label || 'ברקוד'}
                                value={generateBarcodeOnSave ? 'ייווצר בשמירה' : newProductData.Barcode}
                                onChange={(e) => {
                                  setGenerateBarcodeOnSave(false);
                                  handleNewProductChange(e);
                                }}
                                placeholder="הזן ברקוד או לחץ על הכפתור"
                                variant="outlined"
                                fullWidth
                                disabled={createProductMutation.isLoading || generateBarcodeOnSave}
                                InputProps={{
                                  endAdornment: barcodeEnabled ? (
                                    <InputAdornment position="end">
                                      <Tooltip title="צור ברקוד ייחודי">
                                        <span>
                                          <IconButton
                                            edge="end"
                                            size="small"
                                            onClick={() => {
                                              setGenerateBarcodeOnSave(true);
                                              setNewProductData((prev) => ({ ...prev, Barcode: '' }));
                                            }}
                                            disabled={createProductMutation.isLoading}
                                            aria-label="צור ברקוד"
                                          >
                                            <AutoFixHighIcon fontSize="small" />
                                          </IconButton>
                                        </span>
                                      </Tooltip>
                                    </InputAdornment>
                                  ) : null,
                                }}
                              />
                            )}

                            <TextField
                              name="Price"
                              label="מחיר"
                              type="number"
                              value={newProductData.Price}
                              onChange={handleNewProductChange}
                              onWheel={(e) => e.target.blur()}
                              onFocus={(e) => e.target.select()}
                              variant="outlined"
                              fullWidth
                              required
                              disabled={createProductMutation.isLoading}
                              InputProps={{
                                inputProps: { min: 0, step: 0.01 }
                              }}
                            />

                            <TextField
                              name="Description"
                              label="תיאור"
                              value={newProductData.Description}
                              onChange={handleNewProductChange}
                              variant="outlined"
                              fullWidth
                              multiline
                              rows={2}
                              disabled={createProductMutation.isLoading}
                            />

                            <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                              <Button
                                onClick={toggleProductCreationMode}
                                variant="outlined"
                                disabled={createProductMutation.isLoading}
                                fullWidth
                              >
                                ביטול
                              </Button>
                              <Button
                                onClick={handleCreateProduct}
                                variant="contained"
                                color="primary"
                                disabled={createProductMutation.isLoading}
                                fullWidth
                              >
                                {createProductMutation.isLoading ? 'יוצר מוצר...' : 'צור והוסף לרכישה'}
                              </Button>
                            </Box>
                          </Box>
                        </>
                      ) : (
                        <>
                          {/* Product Selection Form */}
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                              <Autocomplete
                                sx={{ flex: 1 }}
                          options={products}
                          getOptionLabel={(option) => option.ProductName}
                          value={selectedProduct}
                          onChange={(_, newValue) => setSelectedProduct(newValue)}
                          filterOptions={filterProductsEnhanced}
                          disableListWrap
                          disabled={isSubmitting}
                          renderInput={(params) => {
                            // Dynamically build placeholder text based on available fields
                            const getPlaceholderText = () => {
                              const searchFields = ['שם מוצר'];
                              if (productFields.includes('CatalogNumber')) {
                                searchFields.push(productFieldsSchema.CatalogNumber?.label || 'מק"ט');
                              }
                              if (productFields.includes('Barcode')) {
                                searchFields.push(productFieldsSchema.Barcode?.label || 'ברקוד');
                              }

                              return searchFields.length > 1
                                ? `הקלד ${searchFields.join(', ')}`
                                : 'הקלד שם מוצר';
                            };

                            return (
                              <TextField
                                {...params}
                                label="מוצר"
                                placeholder={getPlaceholderText()}
                                InputProps={{
                                  ...params.InputProps,
                                  endAdornment: (
                                    <>
                                      {isLoadingProducts ? <CircularProgress size={20} /> : null}
                                      {params.InputProps.endAdornment}
                                    </>
                                  ),
                                }}
                              />
                            );
                          }}
                          renderOption={(props, option) => {
                            // Enhanced rendering
                            const showBarcode = productFields.includes('Barcode');
                            const showCatalogNumber = productFields.includes('CatalogNumber');
                            const showEnhanced = showBarcode || showCatalogNumber || storageEnabled;

                            if (!showEnhanced) {
                              // Simple rendering
                              return (
                                <li {...props}>
                                  <Box>
                                    <Typography variant="body1">{option.ProductName}</Typography>
                                    <Typography variant="body2" color="text.secondary">
                                      מחיר: {new Intl.NumberFormat('he-IL', {
                                        style: 'currency',
                                        currency: 'ILS',
                                      }).format(option.Price || 0)}
                                    </Typography>
                                  </Box>
                                </li>
                              );
                            }

                            // Enhanced rendering with barcode/catalog number
                            return (
                              <li {...props}>
                                <Box sx={{ width: '100%' }}>
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Typography variant="body1">{option.ProductName}</Typography>
                                    {option.matchedField && (
                                      <Chip
                                        label={option.matchedField}
                                        size="small"
                                        variant="outlined"
                                        color="primary"
                                        sx={{ ml: 1 }}
                                      />
                                    )}
                                  </Box>
                                  <Box sx={{ display: 'flex', gap: 2, mt: 0.5 }}>
                                    {showCatalogNumber && option.CatalogNumber && (
                                      <Typography variant="caption" color="text.secondary">
                                        {productFieldsSchema.CatalogNumber?.label || 'מק"ט'}: {option.CatalogNumber}
                                      </Typography>
                                    )}
                                    {showBarcode && option.Barcode && (
                                      <Typography variant="caption" color="text.secondary">
                                        {productFieldsSchema.Barcode?.label || 'ברקוד'}: {option.Barcode}
                                      </Typography>
                                    )}
                                    {option.Price !== undefined && (
                                      <Typography variant="caption" color="text.secondary">
                                        מחיר: {new Intl.NumberFormat('he-IL', {
                                          style: 'currency',
                                          currency: 'ILS',
                                        }).format(option.Price)}
                                      </Typography>
                                    )}
                                  </Box>
                                </Box>
                              </li>
                            );
                          }}
                        />
                              <Button
                                onClick={toggleProductCreationMode}
                                variant="contained"
                                color="secondary"
                                startIcon={<AddIcon />}
                                disabled={isSubmitting}
                                sx={{ width: 'auto', direction: 'ltr' }}
                              >
                                יצור מוצר חדש
                              </Button>
                            </Box>

                        <Box sx={{ display: 'flex', gap: 2 }}>
                          <TextField
                            label="כמות"
                            type="number"
                            value={quantity}
                            onChange={(e) => setQuantity(e.target.value)}
                            onWheel={(e) => e.target.blur()}
                            onFocus={(e) => e.target.select()}
                            disabled={isSubmitting}
                            sx={{ flex: 1 }}
                          />
                          <TextField
                            label="מחיר יחידה"
                            type="number"
                            value={unitPrice}
                            onChange={(e) => setUnitPrice(e.target.value)}
                            onWheel={(e) => e.target.blur()}
                            onFocus={(e) => e.target.select()}
                            disabled={isSubmitting}
                            sx={{ flex: 1 }}
                          />
                        </Box>
                        
                      {storageEnabled && updateInventory && (
                          <Autocomplete
                            options={storages}
                            getOptionLabel={(option) => option.StorageName}
                            value={selectedStorage}
                            onChange={(_, newValue) => setSelectedStorage(newValue)}
                            disabled={isSubmitting}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="מחסן"
                                InputProps={{
                                  ...params.InputProps,
                                  endAdornment: (
                                    <>
                                      {isLoadingStorages ? <CircularProgress size={20} /> : null}
                                      {params.InputProps.endAdornment}
                                    </>
                                  ),
                                }}
                              />
                            )}
                          />
                        )}
                        
                        <TextField
                          label="הערות לפריט"
                          value={itemNotes}
                          onChange={(e) => setItemNotes(e.target.value)}
                          fullWidth
                          disabled={isSubmitting}
                        />

                        <Button
                          variant="contained"
                          startIcon={<AddIcon />}
                          onClick={handleAddItem}
                          disabled={isSubmitting}
                          fullWidth
                        >
                          הוסף פריט
                        </Button>
                          </Box>
                        </>
                      )}
                    </Box>

                    {/* Items List */}
                    {items.length > 0 && (
                      <Paper sx={{ mt: 2 }}>
                        <Table>
                          <TableHead>
                            <TableRow>
                              <TableCell>מוצר</TableCell>
                              <TableCell align="center">כמות</TableCell>
                              <TableCell align="right">מחיר</TableCell>
                              <TableCell align="right">סה"כ</TableCell>
                              {storageEnabled && updateInventory && <TableCell align="center">מחסן</TableCell>}
                              <TableCell align="center"></TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {items.map((item, index) => (
                              <TableRow key={index}>
                                <TableCell>{item.ProductName}</TableCell>
                                <TableCell align="center">{item.Quantity}</TableCell>
                                <TableCell align="right">{item.UnitPrice} ₪</TableCell>
                                <TableCell align="right">{item.Subtotal.toFixed(2)} ₪</TableCell>
                                {storageEnabled && updateInventory && <TableCell align="center">{item.StorageName}</TableCell>}
                                <TableCell align="center">
                                  <IconButton
                                    color="error"
                                    size="small"
                                    onClick={() => handleRemoveItem(index)}
                                    disabled={isSubmitting}
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                </TableCell>
                              </TableRow>
                            ))}
                            <TableRow>
                              <TableCell colSpan={3} align="right" sx={{ fontWeight: 'bold' }}>
                                סכום ביניים:
                              </TableCell>
                              <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                                {calculateSubtotal().toFixed(2)} ₪
                              </TableCell>
                              <TableCell colSpan={storageEnabled && updateInventory ? 2 : 1}></TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                      </Paper>
                    )}

                    <TextField
                      label="משלוח"
                      type="number"
                      value={shippingCost}
                      onChange={(e) => setShippingCost(e.target.value)}
                      onWheel={(e) => e.target.blur()}
                      onFocus={(e) => e.target.select()}
                      disabled={isSubmitting}
                      fullWidth
                    />
                  </>
                ) : (
                  <>
                    {/* Simple mode - just total amount */}
                    <TextField
                      label="סכום כולל"
                      type="number"
                      value={totalAmount}
                      onChange={(e) => setTotalAmount(e.target.value)}
                      onWheel={(e) => e.target.blur()}
                      onFocus={(e) => e.target.select()}
                      disabled={isSubmitting}
                      fullWidth
                      sx={{
                        direction: 'ltr',
                        '& input': {
                          textAlign: 'center',
                        },
                      }}
                    />
                  </>
                )}

                {/* Tax Section - for all modes */}
                <Box sx={{ borderTop: 2, borderColor: 'divider', pt: 2, mt: 2 }}>
                  <FormControl component="fieldset" fullWidth>
                    <FormLabel component="legend">מע"מ</FormLabel>
                    <RadioGroup
                      row
                      value={taxState}
                      onChange={(e) => setTaxState(e.target.value)}
                    >
                      <FormControlLabel value="none" control={<Radio />} label="ללא" disabled={isSubmitting} />
                      <FormControlLabel value="included" control={<Radio />} label='כולל מע"מ' disabled={isSubmitting} />
                      <FormControlLabel value="excluded" control={<Radio />} label='חייב במע"מ - לא כולל' disabled={isSubmitting} />
                    </RadioGroup>
                    {taxState !== 'none' && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        שיעור מע"מ: {taxRate}%
                      </Typography>
                    )}
                  </FormControl>

                  {/* Summary */}
                  <Box sx={{ mt: 2, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                    {specifyItems ? (
                      <>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography>סכום ביניים:</Typography>
                          <Typography>{calculateSubtotal().toFixed(2)} ₪</Typography>
                        </Box>

                        {parseFloat(shippingCost) > 0 && (
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Typography>משלוח:</Typography>
                            <Typography>+{parseFloat(shippingCost).toFixed(2)} ₪</Typography>
                          </Box>
                        )}

                        {taxState === 'included' && taxAmount > 0 && (
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="body2" color="text.secondary">
                              מזה מע"מ ({taxRate}%):
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {taxAmount.toFixed(2)} ₪
                            </Typography>
                          </Box>
                        )}

                        {taxState === 'excluded' && taxAmount > 0 && (
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Typography>
                              מע"מ ({taxRate}%):
                            </Typography>
                            <Typography>
                              +{taxAmount.toFixed(2)} ₪
                            </Typography>
                          </Box>
                        )}
                      </>
                    ) : (
                      <>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography>סכום שהוזן:</Typography>
                          <Typography>{parseFloat(totalAmount || 0).toFixed(2)} ₪</Typography>
                        </Box>

                        {taxState === 'included' && taxAmount > 0 && (
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="body2" color="text.secondary">
                              מזה מע"מ ({taxRate}%):
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {taxAmount.toFixed(2)} ₪
                            </Typography>
                          </Box>
                        )}

                        {taxState === 'excluded' && taxAmount > 0 && (
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Typography>
                              מע"מ ({taxRate}%):
                            </Typography>
                            <Typography>
                              +{taxAmount.toFixed(2)} ₪
                            </Typography>
                          </Box>
                        )}
                      </>
                    )}

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                      <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                        סה"כ לתשלום:
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                        {calculateTotal().toFixed(2)} ₪
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                <TextField
                  label="הערות"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  multiline
                  minRows={2}
                  fullWidth
                  disabled={isSubmitting}
                />

                {/* Optional: record a payment for this purchase in the same dialog */}
                <Divider sx={{ my: 1 }} />
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                    שלם כעת לספק
                  </Typography>
                  <Switch
                    checked={recordPaymentNow}
                    onChange={(e) => setRecordPaymentNow(e.target.checked)}
                    disabled={isSubmitting}
                  />
                </Box>
                <Collapse in={recordPaymentNow} unmountOnExit>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 2,
                      mt: 2,
                      p: 2,
                      bgcolor: 'action.hover',
                      borderRadius: 1,
                    }}
                  >
                    <TextField
                      label={`סכום תשלום${isNonILS ? ` (${currencySymbol})` : ''}`}
                      type="number"
                      value={paymentAmount}
                      onChange={(e) => {
                        setPaymentAmount(e.target.value);
                        setPaymentTouched(true);
                      }}
                      onWheel={(e) => e.target.blur()}
                      onFocus={(e) => e.target.select()}
                      disabled={isSubmitting}
                      helperText={`ברירת מחדל: סכום הרכישה (${calculateTotal().toFixed(2)} ${currencySymbol}). ניתן לשנות לתשלום חלקי.`}
                      sx={{ direction: 'ltr', '& input': { textAlign: 'center' } }}
                    />
                    <TextField
                      label="הערה לתשלום"
                      value={paymentNote}
                      onChange={(e) => setPaymentNote(e.target.value)}
                      fullWidth
                      disabled={isSubmitting}
                    />
                    <PaymentMethodRows
                      paymentMethods={paymentMethods}
                      setPaymentMethods={setPaymentMethods}
                      totalAmount={parseFloat(paymentAmount) || 0}
                      disabled={isSubmitting}
                      compact={false}
                      currencySymbol={currencySymbol}
                    />
                  </Box>
                </Collapse>
              </>
            )}

            {formStep === 'confirm' && (
              <>
                {isSubmitting ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, justifyContent: 'center', py: 2 }}>
                    <CircularProgress size={24} />
                    <Typography>מעדכן...</Typography>
                  </Box>
                ) : (
                  <>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                      אישור רכישה
                    </Typography>
                    <Typography>ספק: {supplier?.SupplierName}</Typography>
                    {invoiceNumber && <Typography>חשבונית: {invoiceNumber}</Typography>}
                    {shippingDocument && <Typography>תעודת משלוח: {shippingDocument}</Typography>}
                    
                    {specifyItems ? (
                      <>
                        <Typography>מספר פריטים: {items.length}</Typography>
                        <Typography>סכום ביניים: {calculateSubtotal().toFixed(2)} ₪</Typography>
                        {parseFloat(shippingCost) > 0 && <Typography>משלוח: +{parseFloat(shippingCost).toFixed(2)} ₪</Typography>}

                        {taxState === 'included' && taxAmount > 0 && (
                          <Typography variant="body2" color="text.secondary">
                            מזה מע"מ ({taxRate}%): {taxAmount.toFixed(2)} ₪
                          </Typography>
                        )}

                        {taxState === 'excluded' && taxAmount > 0 && (
                          <Typography>
                            מע"מ ({taxRate}%): +{taxAmount.toFixed(2)} ₪
                          </Typography>
                        )}

                        <Typography sx={{ fontWeight: 'bold', fontSize: '1.2rem' }}>
                          סה"כ: {calculateTotal().toFixed(2)} ₪
                        </Typography>
                        {storageEnabled && (
                          <Typography sx={{ color: updateInventory ? 'green' : 'orange' }}>
                            {updateInventory ? '✓ המלאי יעודכן' : '✗ המלאי לא יעודכן'}
                          </Typography>
                        )}
                      </>
                    ) : (
                      <>
                        <Typography>סכום שהוזן: {parseFloat(totalAmount || 0).toFixed(2)} ₪</Typography>

                        {taxState === 'included' && taxAmount > 0 && (
                          <Typography variant="body2" color="text.secondary">
                            מזה מע"מ ({taxRate}%): {taxAmount.toFixed(2)} ₪
                          </Typography>
                        )}

                        {taxState === 'excluded' && taxAmount > 0 && (
                          <Typography>
                            מע"מ ({taxRate}%): +{taxAmount.toFixed(2)} ₪
                          </Typography>
                        )}

                        <Typography sx={{ fontWeight: 'bold', fontSize: '1.2rem' }}>
                          סה"כ: {calculateTotal().toFixed(2)} ₪
                        </Typography>

                        <Typography sx={{ color: 'gray', fontSize: '0.9rem' }}>
                          (רכישה כללית ללא פירוט מוצרים)
                        </Typography>
                      </>
                    )}
                    {notes && <Typography>הערות: {notes}</Typography>}

                    {recordPaymentNow && (
                      <Box sx={{ mt: 2, p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                          תשלום בו-זמני
                        </Typography>
                        <Typography>
                          סכום: {currencySymbol}{parseFloat(paymentAmount || 0).toFixed(2)}
                        </Typography>
                        {paymentNote && <Typography>הערה: {paymentNote}</Typography>}
                        {paymentMethods.length > 0 && (
                          <>
                            <Typography sx={{ mt: 1, fontWeight: 'bold' }}>
                              אמצעי תשלום:
                            </Typography>
                            {paymentMethods.map((pm, index) => {
                              const typeLabels = {
                                cash: 'מזומן',
                                check: "צ'ק",
                                credit_card: 'כרטיס אשראי',
                                bank_transfer: 'העברה בנקאית',
                                other: 'אחר'
                              };
                              let details = '';
                              if (pm.type === 'check' && pm.check_number) {
                                details = ` - צ'ק #${pm.check_number}`;
                                if (pm.check_bank_name) details += ` (${pm.check_bank_name})`;
                              } else if (pm.type === 'credit_card' && pm.card_last_four) {
                                details = ` - ****${pm.card_last_four}`;
                              } else if (pm.type === 'bank_transfer' && pm.bank_name) {
                                details = ` - ${pm.bank_name}`;
                              }
                              return (
                                <Typography key={index}>
                                  {typeLabels[pm.type] || pm.type}: {parseFloat(pm.amount).toFixed(2)} {currencySymbol}{details}
                                </Typography>
                              );
                            })}
                          </>
                        )}
                      </Box>
                    )}
                  </>
                )}
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} size="large" disabled={isSubmitting}>
            ביטול
          </Button>
          {formStep === 'form' && (
            <Button
              variant="contained"
              onClick={handleValidation}
              size="large"
              disabled={isSubmitting}
            >
              שלב הבא
            </Button>
          )}
          {formStep === 'confirm' && (
            <>
              <Button
                onClick={() => setFormStep('form')}
                size="large"
                disabled={isSubmitting}
              >
                חזרה
              </Button>
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={isSubmitting}
                size="large"
              >
                {isSubmitting ? <CircularProgress size={24} /> : 'אישור'}
              </Button>
            </>
          )}
        </DialogActions>
      </MobileDialog>
    </LocalizationProvider>
  );
};

export default SupplierPurchaseDialog;